import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Column, DataGrid } from '../components/DataGrid';
import { Flag } from '../components/Flag';
import { mapState, STATE_LABEL, STATE_ORDER, StateWord } from '../components/VehicleState';
import { Link } from '../components/Links';
import { api, UnitStatus, Vehicle } from '../api';
import { BackState } from '../components/Panels';
import { UnitStatusPill } from '../components/UnitStatusPill';
import { ago, dateTime, TYPE_LABEL, UNIT_STATUS_LABEL } from '../format';

// The vehicles as a data grid (interface refresh, screen 6): the whole row opens the vehicle
// page; links inside it still work on their own. Also used on the customer card.
export function VehicleTable({ rows, back, showCustomer = true }: { rows: Vehicle[]; back: BackState; showCustomer?: boolean }) {
  const columns: Column<Vehicle>[] = [
    { key: 'name', label: 'Name', always: true, value: (v) => v.name,
      cell: (v) => <Link to={`/vehicles/${v.id}`} state={back}><strong>{v.name}</strong></Link> },
    { key: 'reg', label: 'Registration', value: (v) => (v.registration && v.registration !== v.name ? v.registration : null), nowrap: true },
    { key: 'type', label: 'Type', value: (v) => TYPE_LABEL[v.type] },
    ...(showCustomer ? [{ key: 'customer', label: 'Customer', value: (v: Vehicle) => v.customer?.name ?? null,
      cell: (v: Vehicle) => (v.customer ? <Link to={`/customers/${v.customer.id}`}>{v.customer.name}</Link> : <span className="muted">–</span>) }] : []),
    { key: 'state', label: 'Now', value: (v) => STATE_LABEL[mapState(v)], sortBy: (v) => STATE_ORDER.indexOf(mapState(v)),
      cell: (v) => <StateWord state={mapState(v)} />, nowrap: true },
    { key: 'tracker', label: 'Tracker', value: (v) => v.tracker?.imei ?? null,
      cell: (v) => (v.tracker ? <Link className="mono" to={`/trackers/${v.tracker.id}`} state={back}>{v.tracker.imei}</Link> : <span className="muted">none fitted</span>) },
    { key: 'status', label: 'Tracker status', value: (v) => (v.trackerStatus ? UNIT_STATUS_LABEL[v.trackerStatus] : null),
      cell: (v) => (v.trackerStatus ? <UnitStatusPill status={v.trackerStatus} /> : <span className="muted">–</span>) },
    { key: 'last', label: 'Last data', value: (v) => v.lastReceived, sortBy: (v) => (v.lastReceived ? Date.parse(v.lastReceived) : null),
      csv: (v) => (v.lastReceived ? dateTime(v.lastReceived) : null), cell: (v) => (v.lastReceived ? <span title={dateTime(v.lastReceived)}>{ago(v.lastReceived)}</span> : <span className="muted">–</span>), nowrap: true },
    { key: 'today', label: 'Today (km)', align: 'right', value: (v) => (v.todayKm === null ? null : Math.round(v.todayKm * 10) / 10) },
    { key: 'speed', label: 'Speed (km/h)', align: 'right', hidden: true, value: (v) => (v.position?.speedKmh == null ? null : Math.round(v.position.speedKmh)) },
    { key: 'volt', label: 'Voltage (V)', align: 'right', hidden: true, value: (v) => (v.position?.powerV == null ? null : Math.round(v.position.powerV * 10) / 10) },
    { key: 'country', label: 'Country', hidden: true, value: (v) => v.country?.name ?? null,
      cell: (v) => (v.country ? <><Flag country={v.country.code} name={v.country.name} /> {v.country.name}</> : <span className="muted">–</span>) },
    { key: 'pos', label: 'Position', hidden: true, value: (v) => (v.position ? `${v.position.lat.toFixed(5)}, ${v.position.lon.toFixed(5)}` : null), nosort: true },
  ];
  return <DataGrid id={showCustomer ? 'vehicles' : 'customer-vehicles'} rows={rows} columns={columns} rowKey={(v) => v.id} noun={['vehicle', 'vehicles']}
    href={(v) => `/vehicles/${v.id}`} rowState={back} defaultSort={{ key: 'name', dir: 1 }} />;
}

export function Vehicles() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const [rows, setRows] = useState<Vehicle[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.vehicles().then((r) => setRows(r.vehicles)).catch((e) => setError(e.message));
  }, []);

  const set = (key: string, value: string) =>
    setParams((p) => { value ? p.set(key, value) : p.delete(key); return p; }, { replace: true });

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows?.filter((v) => (!status || (status === 'none' ? !v.tracker : v.trackerStatus === status))
      && (!needle || [v.name, v.registration, v.tracker?.imei, v.customer?.name].some((s) => s?.toLowerCase().includes(needle)))) ?? null;
  }, [rows, q, status]);
  const counts = useMemo(() => rows?.reduce<Record<string, number>>((c, v) => {
    const k = v.trackerStatus ?? 'none';
    return { ...c, [k]: (c[k] ?? 0) + 1 };
  }, {}) ?? {}, [rows]);

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Vehicles</h1>
        <Link className="button" to="/install">Install a tracker</Link>
      </div>
      <p className="muted small">New vehicles are added by installing a tracker in them.</p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="toolbar">
        <label className="sr-only" htmlFor="veh-search">Search</label>
        <input id="veh-search" type="search" placeholder="Search name, reg, IMEI, customer…" value={q} onChange={(e) => set('q', e.target.value)} />
        <label className="sr-only" htmlFor="veh-status">Tracker status</label>
        <select id="veh-status" value={status} onChange={(e) => set('status', e.target.value)}>
          <option value="">All ({rows?.length ?? 0})</option>
          {(Object.keys(UNIT_STATUS_LABEL) as UnitStatus[]).map((k) => <option key={k} value={k}>{UNIT_STATUS_LABEL[k]} ({counts[k] ?? 0})</option>)}
          <option value="none">No tracker ({counts.none ?? 0})</option>
        </select>
      </div>
      {shown && <VehicleTable rows={shown} back={{ from: location.pathname + location.search, fromLabel: 'Vehicles' }} />}
    </div>
  );
}
